import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { ClassesEntity } from '../../../../../../models/entities/classes-entity';

@Component({
  selector: 'app-settings-tab',
  templateUrl: './settings-tab.component.html',
  styleUrls: ['./settings-tab.component.css'],
})
export class SettingsTabComponent implements OnChanges {
  @Input() classEntity!: ClassesEntity;
  @Input() classId!: string;
  @Input() hasVmResource = false;
  @Input() hasAccResource = false;
  /** Number of enrolled participants — forwarded to acc-subscription-settings for removal guard. */
  @Input() participantCount = 0;
  /** True once the template VM is provisioned (CREATED or SNAPSHOTTED state). */
  @Input() isVmTemplateReady = false;
  /** True once a snapshot has been created ("Testing Done"). Gates forward action. */
  @Input() hasSnapshot = false;
  /** True when the current user has cloud-settings rights (enables Testing Done button). */
  @Input() hasCloudSettings = false;
  /** Set by parent to deep-link into a named sub-section on first render.
   * Cleared via initialSectionConsumed once applied. Valid names:
   * tenant | vm-subscriptions | vm-template | account-subscriptions | acc-template | schedule | participants | forward */
  @Input() initialSection: string | null = null;

  /** Propagated up to ClassDetailV2Component when the snapshot succeeds. */
  @Output() snapshotDone = new EventEmitter<void>();
  /** Emitted once `initialSection` has been applied so the parent can clear it. */
  @Output() initialSectionConsumed = new EventEmitter<void>();
  /** Emitted when VM subscriptions are added so the parent can silently re-fetch classEntity. */
  @Output() subscriptionsChanged = new EventEmitter<void>();
  /** Emitted when ACC subscriptions are added/removed so the parent can silently re-fetch classEntity. */
  @Output() accSubscriptionsChanged = new EventEmitter<void>();
  /** Emitted when tenant credentials are saved so the parent can silently re-fetch classEntity. */
  @Output() tenantConfigured = new EventEmitter<void>();
  /**
   * Emitted when the Forward settings tab becomes active so the parent can silently re-fetch
   * classEntity. This ensures the forward gate reads fresh `pair.Status` values after any
   * background liveness revert has been written to the class document.
   */
  @Output() classDataRefreshNeeded = new EventEmitter<void>();

  selectedSectionIndex = 0;

  /**
   * REMAINING provisionable-VM count across all VM subscriptions (limit − current usage),
   * as last reported by VmSubscriptionSettingsComponent.  null = quota is still loading.
   * Informational only — surfaced in the participant-capacity drawer as "provisionable now".
   * NOT used to derive the participant cap (see totalVmGrossCapacity).
   */
  totalVmCapacity: number | null = null;

  /**
   * GROSS class-VM slot count across all VM subscriptions — the maximum number of class VMs
   * the subscriptions can ever hold, independent of current Azure core usage.
   * null = quota is still loading. This is what the participant cap is derived from.
   */
  totalVmGrossCapacity: number | null = null;

  /** True when any contributing subscription's quota cache is stale. */
  anyVmQuotaStale = false;

  /** Receives the REMAINING VM capacity signal from the VM-subscriptions child component. */
  onVmCapacityChanged(total: number | null): void {
    this.totalVmCapacity = total;
  }

  /** Receives the GROSS VM slot-count signal from the VM-subscriptions child component. */
  onVmGrossCapacityChanged(total: number | null): void {
    this.totalVmGrossCapacity = total;
  }

  /** Receives the stale-quota signal from the VM-subscriptions child component. */
  onVmQuotaStaleChanged(stale: boolean): void {
    this.anyVmQuotaStale = stale;
  }

  /**
   * The absolute maximum number of participants this class can support, derived from the
   * intersection of VM quota and Account Subscription capacity.
   *
   * Formula:
   *   vmWise  = Sum(gross VM slots per VM sub) − 1
   *             The −1 is a permanent reservation for the class-template VM/AAD user; it
   *             applies regardless of whether the template VM has been created yet, because
   *             the gross slot count is usage-independent (it does not shrink as participant
   *             VMs are provisioned).
   *   accWise = (ACC sub count) − 1    (1 sub reserved for the class-template AAD user)
   *   result  = Min(vmWise, accWise)  — whichever constraint is binding
   *
   * Returns null when:
   *   • the class has neither a VM nor an ACC resource (genuinely unconstrained), OR
   *   • VM quota data is still loading (caller should treat as unconstrained to avoid flicker)
   */
  get maxEffectiveParticipants(): number | null {
    let vmWise: number | null = null;
    if (this.hasVmResource) {
      // Quota still loading — treat as unconstrained so the participant tab never blocks
      // before data arrives.
      if (this.totalVmGrossCapacity === null) return null;
      vmWise = Math.max(0, this.totalVmGrossCapacity - 1);
    }

    let accWise: number | null = null;
    if (this.hasAccResource) {
      const accSubs: any[] = this.classEntity?.data?.['AccSubscription'] ?? [];
      // -1 reserves one ACC sub for the class-template AAD user provisioning.
      accWise = Math.max(0, accSubs.length - 1);
    }

    // Both null → no resource-based capacity constraint on this class
    if (vmWise === null && accWise === null) return null;
    if (vmWise === null) return accWise;
    if (accWise === null) return vmWise;
    return Math.min(vmWise, accWise);
  }

  /**
   * VM-derived participant slot count: gross VM slots − 1 (permanent class-template
   * reservation). Usage-independent, so it does not shrink as participant VMs are provisioned.
   * null when VM quota is still loading or the class has no VM resource type.
   * Passed to the participants tab for the capacity-breakdown drawer.
   */
  get vmWiseCapacity(): number | null {
    if (!this.hasVmResource) return null;
    if (this.totalVmGrossCapacity === null) return null;
    return Math.max(0, this.totalVmGrossCapacity - 1);
  }

  /**
   * REMAINING provisionable VMs right now (limit − usage), passed to the participants tab
   * as an informational "provisionable now" stat. null when loading / no VM resource.
   */
  get remainingVmCapacity(): number | null {
    if (!this.hasVmResource) return null;
    return this.totalVmCapacity;
  }

  /**
   * ACC-derived participant slot count (ACC sub count − 1 for template reservation).
   * null when the class has no ACC resource type.
   * Passed to the participants tab for the capacity-breakdown drawer.
   */
  get accWiseCapacity(): number | null {
    if (!this.hasAccResource) return null;
    const accSubs: any[] = this.classEntity?.data?.['AccSubscription'] ?? [];
    return Math.max(0, accSubs.length - 1);
  }

  /**
   * True while the VM subscription quota data is being fetched.
   * Passed to the participants tab so it can render a 'calculating capacity' spinner
   * instead of a hard-blocked state.
   */
  get isCapacityLoading(): boolean {
    return this.hasVmResource && this.totalVmGrossCapacity === null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialSection'] && this.initialSection) {
      this.selectedSectionIndex = this.sectionIndex(this.initialSection);
      // Defer the emission by one tick so the parent mutation (settingsInitialSection = null)
      // happens outside the current change-detection cycle and avoids NG0100.
      setTimeout(() => this.initialSectionConsumed.emit());
    }
  }

  /**
   * Returns the dynamic mat-tab index for a named settings sub-section.
   * Tab order (Faculty permanently hidden):
   *   0: Tenant | [1: VM Subs] | [2: VM Template] | [?: Acc Subs] | [?: Acc Template] |
   *   ?: Schedule | ?: Participants | ?: Forward
   *
   * The VM Connection Template panel was promoted into the VM Template tab itself
   * (post-provisioning gating) and the standalone "VM Connection Settings" sub-tab
   * was removed in round-3 — so `vmOffset` reverts to 2 and we no longer track
   * a `vm-connection-settings` named entry.
   */
  sectionIndex(name: string): number {
    const vmOffset  = this.hasVmResource  ? 2 : 0;
    const accOffset = this.hasAccResource ? 2 : 0;
    const map: Record<string, number> = {
      'tenant':                  0,
      'vm-subscriptions':        this.hasVmResource  ? 1 : 0,
      'vm-template':             this.hasVmResource  ? 2 : 0,
      'account-subscriptions':   this.hasAccResource ? (1 + vmOffset) : 0,
      'acc-template':            this.hasAccResource ? (2 + vmOffset) : 0,
      'schedule':                1 + vmOffset + accOffset,
      'participants':            2 + vmOffset + accOffset,
      'forward':                 3 + vmOffset + accOffset,
    };
    return map[name.toLowerCase()] ?? 0;
  }

  navigateToVmTemplate(): void {
    this.selectedSectionIndex = this.sectionIndex('vm-template');
  }

  navigateToVmSubscriptions(): void {
    this.selectedSectionIndex = this.sectionIndex('vm-subscriptions');
  }

  navigateToTenant(): void {
    this.selectedSectionIndex = this.sectionIndex('tenant');
  }

  navigateToAccTemplate(): void {
    this.selectedSectionIndex = this.sectionIndex('acc-template');
  }

  navigateToAccSubscriptions(): void {
    this.selectedSectionIndex = this.sectionIndex('account-subscriptions');
  }

  /**
   * Called by mat-tab-group on every tab activation. Emits `classDataRefreshNeeded`
   * when the Forward tab is activated so the parent can silently re-fetch classEntity,
   * ensuring the gate reads post-reconciliation `pair.Status` values.
   */
  onSectionActivated(newIndex: number): void {
    if (newIndex === this.sectionIndex('forward')) {
      this.classDataRefreshNeeded.emit();
    }
  }

  /** True when at least one ACC subscription has been added to the class. */
  get hasAccSubscriptions(): boolean {
    const subs: any[] = this.classEntity?.data?.['AccSubscription'] ?? [];
    return subs.length > 0;
  }

  /**
   * True when the class `Stage` has been advanced to `"Confirm"` by the auto-confirmation
   * pipeline (all templates tested → `TryLockConfirmationAsync` succeeded).
   * When true the forward gate is fully lifted regardless of individual template states.
   */
  get isClassConfirmed(): boolean {
    const stage: string = (this.classEntity as any)?.data?.Stage ?? '';
    return stage.toLowerCase() === 'confirm';
  }

  /**
   * True when the VM template for this class has been marked "tested" by the trainer.
   * Reads the `status` field on the VM entry in `resourceTemplateIds` (set server-side by
   * `MarkTestedAsync`).  Returns `true` trivially when the class has no VM resource so
   * that the absence of a VM template never blocks the forward action.
   */
  get isVmTested(): boolean {
    if (!this.hasVmResource) return true;
    const templates: any[] = (this.classEntity as any)?.trainingSchedule?.resourceTemplateIds ?? [];
    const vm = templates.find(
      (t: any) => (t?.resourceType ?? t?.type ?? '').toLowerCase() === 'vm',
    );
    return (vm?.status ?? vm?.Status ?? '').toLowerCase() === 'tested';
  }

  /**
   * True when the ACC template for this class has been marked "tested" by the trainer.
   * Reads the `status` field on the ACC entry in `resourceTemplateIds` (set server-side by
   * `MarkTestedAsync`).  Returns `true` trivially when the class has no ACC resource so
   * that the absence of an ACC template never blocks the forward action.
   */
  get isAccTested(): boolean {
    if (!this.hasAccResource) return true;
    const templates: any[] = (this.classEntity as any)?.trainingSchedule?.resourceTemplateIds ?? [];
    const acc = templates.find(
      (t: any) => (t?.resourceType ?? t?.type ?? '').toLowerCase() === 'acc',
    );
    return (acc?.status ?? acc?.Status ?? '').toLowerCase() === 'tested';
  }
}
